import { Component, Input, ElementRef, HostListener } from '@angular/core';
import { CommonModule } from '@angular/common';
import { steps as defaultSteps, atc_steps } from 'src/app/modules/globals/dash/modules/pages/components/pre-race/steps';

interface PreparationStep {
  status: string;
  comments?: string;
  last_updated?: string;
}

interface RaceStatus {
  track_assignments?: {
    status: string;
    complete: boolean;
    tracks_assigned: number;
    total_races: number;
  };
  rail_position?: {
    status: string;
    complete: boolean;
    rail_positions_set: number;
    is_configured: boolean;
  };
  kit_assignment?: {
    status: string;
    complete: boolean;
    kit_id: string;
    is_tag_set: boolean;
  };
  startlist?: {
    status: string;
    complete: boolean;
    pdf_generated: boolean;
    has_errors: boolean;
    email_sent: boolean;
  };
  event_state?: {
    status: string;
  };
}

interface RacePreparation {
  completion_percentage: number;
  status: RaceStatus;
}

interface PreparationData {
  completion_percentage: number;
  steps: { [key: string]: PreparationStep };
}

interface EventData {
  venue_name: string;
  external_event_id: string;
  event_details?: {
    venue_name: string;
    event_id: string;
    event_unique_id: string;
    is_atc: boolean;
  };
  race_preparation?: {
    status?: {
      event_state?: {
        status: string;
      };
    };
  };
}

@Component({
  selector: 'app-tooltip',
  template: `
    <div class="tooltip-trigger" (mouseenter)="show()" (mouseleave)="hide()">
      <ng-content></ng-content>
      <div class="tooltip-content" [class.visible]="isVisible" [class.position-{{position}}]="true">
        {{ content }}
      </div>
    </div>
  `,
  styles: [`
    .tooltip-trigger {
      position: relative;
      display: inline-block;
    }

    .tooltip-content {
      position: absolute;
      background: #1a1a1a;
      color: white;
      padding: 8px 12px;
      border-radius: 6px;
      font-size: 12px;
      line-height: 1.4;
      white-space: pre-line;
      z-index: 1000;
      max-width: 300px;
      opacity: 0;
      visibility: hidden;
      transform: translateY(10px);
      transition: all 0.2s ease;
      box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
    }

    .tooltip-content.visible {
      opacity: 1;
      visibility: visible;
      transform: translateY(0);
    }

    .tooltip-content::before {
      content: '';
      position: absolute;
      width: 0;
      height: 0;
      border-style: solid;
    }

    .tooltip-content.position-top {
      bottom: calc(100% + 10px);
      left: 50%;
      transform: translateX(-50%);
    }

    .tooltip-content.position-top::before {
      bottom: -5px;
      left: 50%;
      transform: translateX(-50%);
      border-width: 5px 5px 0 5px;
      border-color: #1a1a1a transparent transparent transparent;
    }

    .tooltip-content.position-bottom {
      top: calc(100% + 10px);
      left: 50%;
      transform: translateX(-50%);
    }

    .tooltip-content.position-bottom::before {
      top: -5px;
      left: 50%;
      transform: translateX(-50%);
      border-width: 0 5px 5px 5px;
      border-color: transparent transparent #1a1a1a transparent;
    }

    .tooltip-content.position-left {
      right: calc(100% + 10px);
      top: 50%;
      transform: translateY(-50%);
    }

    .tooltip-content.position-left::before {
      right: -5px;
      top: 50%;
      transform: translateY(-50%);
      border-width: 5px 0 5px 5px;
      border-color: transparent transparent transparent #1a1a1a;
    }

    .tooltip-content.position-right {
      left: calc(100% + 10px);
      top: 50%;
      transform: translateY(-50%);
    }

    .tooltip-content.position-right::before {
      left: -5px;
      top: 50%;
      transform: translateY(-50%);
      border-width: 5px 5px 5px 0;
      border-color: transparent #1a1a1a transparent transparent;
    }

    @media (prefers-color-scheme: dark) {
      .tooltip-content {
        background: #2d3748;
        color: #f7fafc;
      }

      .tooltip-content.position-top::before {
        border-color: #2d3748 transparent transparent transparent;
      }

      .tooltip-content.position-bottom::before {
        border-color: transparent transparent #2d3748 transparent;
      }

      .tooltip-content.position-left::before {
        border-color: transparent transparent transparent #2d3748;
      }

      .tooltip-content.position-right::before {
        border-color: transparent #2d3748 transparent transparent;
      }
    }
  `]
})
export class TooltipComponent {
  @Input() content: string = '';
  @Input() position: 'top' | 'bottom' | 'left' | 'right' = 'top';

  isVisible = false;

  show() {
    this.isVisible = true;
  }

  hide() {
    this.isVisible = false;
  }

  getStepName(type: 'login' | 'logout', index: number, isAtc: boolean = false): string {
    const stepsArray = isAtc ? atc_steps[type] : defaultSteps[type];
    return stepsArray && stepsArray[index - 1] ? stepsArray[index - 1].name : `Step ${index}`;
  }

  raceSteps = [
    { key: 'track_assignments', icon: 'road', title: 'Track Assignments' },
    { key: 'rail_position', icon: 'ruler-horizontal', title: 'Rail Configuration' },
    { key: 'kit_assignment', icon: 'toolbox', title: 'Kit Assignment' },
    { key: 'startlist', icon: 'file-alt', title: 'Startlist Generation' }
  ];

  isRacePreparation(data: any): data is RacePreparation {
    return data && 'status' in data && 'completion_percentage' in data;
  }

  isPreparationData(data: any): data is PreparationData {
    return data && 'steps' in data && 'completion_percentage' in data;
  }

  isEventData(data: any): data is EventData {
    return data && ('venue_name' in data || 'external_event_id' in data || 'event_details' in data);
  }

  getStatusIconClass(status: any): string {
    if (!status) return 'info';
    const statusStr = typeof status === 'string' ? status.toUpperCase() : status.status?.toUpperCase();
    if (statusStr === 'COMPLETED') return 'success';
    if (statusStr === 'FAILED') return 'danger';
    if (statusStr === 'IN_PROGRESS') return 'warning';
    return 'info';
  }

  getPreparationIconClass(data: RacePreparation | PreparationData | EventData): string {
    if (this.isPreparationData(data) && data.steps) {
      const steps = Object.values(data.steps);
      const failedSteps = steps.filter(s => s.status === 'FAILED').length;
      const completedSteps = steps.filter(s => s.status === 'COMPLETED').length;
      if (failedSteps > 0) return 'danger';
      if (completedSteps === steps.length) return 'success';
      return 'warning';
    }
    return 'info';
  }

  getStatusClass(status: string | undefined): string {
    if (!status) return 'info';
    switch (status.toUpperCase()) {
      case 'COMPLETED': return 'success';
      case 'FAILED': return 'danger';
      case 'IN_PROGRESS': return 'warning';
      default: return 'info';
    }
  }

  getStepIcon(status: string | undefined): string {
    if (!status) return 'clock';
    switch (status.toUpperCase()) {
      case 'COMPLETED': return 'check';
      case 'FAILED': return 'times';
      default: return 'clock';
    }
  }

  getStepStatus(step: PreparationStep | undefined): string {
    return step?.status || '';
  }
}
